/**
 * MODULE: platform/browser/frontend/components/BrowserMissionPicker.tsx
 * PURPOSE: Quick-start tiles for CRM, LinkedIn, Salesforce, and web research missions.
 */

import type { BrowserMission } from '../data/browserMissions';

type BrowserMissionPickerProps = {
  missions: BrowserMission[];
  disabled?: boolean;
  onSelect: (mission: BrowserMission) => void;
};

function MissionIcon({ icon }: { icon: BrowserMission['icon'] }) {
  const label =
    icon === 'crm'
      ? 'CRM'
      : icon === 'linkedin'
        ? 'in'
        : icon === 'salesforce'
          ? 'SF'
          : icon === 'social'
            ? '@'
            : '⌕';
  return (
    <span
      className="flex h-9 w-9 items-center justify-center rounded-lg bg-white text-xs font-bold text-sky-700 shadow-sm dark:bg-gray-800 dark:text-sky-300"
      aria-hidden
    >
      {label}
    </span>
  );
}

export function BrowserMissionPicker({ missions, disabled, onSelect }: BrowserMissionPickerProps) {
  return (
    <div className="grid gap-2 sm:grid-cols-2">
      {missions.map((mission) => (
        <button
          key={mission.id}
          type="button"
          disabled={disabled}
          data-testid={`browser-mission-${mission.id}`}
          onClick={() => onSelect(mission)}
          className="flex items-start gap-3 rounded-xl border border-gray-200 bg-gray-50 p-3 text-left transition hover:border-sky-300 hover:bg-sky-50/80 disabled:opacity-50 dark:border-gray-700 dark:bg-gray-800/60 dark:hover:border-sky-700 dark:hover:bg-sky-950/30"
        >
          <MissionIcon icon={mission.icon} />
          <span>
            <span className="block text-sm font-semibold text-gray-900 dark:text-white">
              {mission.label}
            </span>
            <span className="mt-0.5 block text-xs text-gray-600 dark:text-gray-400">
              {mission.description}
            </span>
          </span>
        </button>
      ))}
    </div>
  );
}
